import { useEffect } from 'react';

import { formatMoney } from '@betterment/js-runtime';
import { Box, FormikField as Field, Input } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { LegacyTransferType } from '../../../../graphql/schema';
import { NarrowTypename } from '../../../../graphql/utils/narrow-typename';
import {
  handleSetupOneTimeDeposit,
  handleSetupRecurringDeposit,
} from '../../../purpose/components/settings-card/utils';
import {
  calculateCashflows,
  convertToMoneyWithDefault,
  hasMaskedFormFieldChanged,
} from '../../../shared/helpers/utils';
import { useForecasterAccountInputs } from '../../context/ForecasterAccountInputsContext';
import { ForecasterAccountSettingsQuery } from '../../graphql/forecasterAccountSettings.generated';

const schema = Yup.object().shape({
  adHocInflow: Yup.string().optional(),
  monthlyRecurringInflows: Yup.string().optional(),
});

export const FormikForm = ({ subAccountId }: { subAccountId: string }) => {
  const { handleSubmit, initialValues, submitForm, validateForm, values } = useFormikContext<{
    adHocInflow: string;
    monthlyRecurringDeposits: string;
  }>();

  useEffect(() => {
    void validateForm();
    void submitForm();
  }, []);

  const handleFormChange = async () => {
    await validateForm();
    handleSubmit();
  };

  return (
    <Form>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        <Field name="adHocInflow">
          <Field.Label>One-time contribution</Field.Label>
          <Field.Control>
            <Input
              leadingSymbol="$"
              mask="number"
              placeholder="0"
              aria-label="One time contribution amount"
              onChange={handleFormChange}
            />
          </Field.Control>
          <Field.ErrorMessage />
        </Field>
        {values.adHocInflow && hasMaskedFormFieldChanged(values.adHocInflow, initialValues.adHocInflow) && (
          <SambaAutoLink to={handleSetupOneTimeDeposit(subAccountId, values.adHocInflow)}>
            Continue to deposit
          </SambaAutoLink>
        )}
        <Box>
          <Field name="monthlyRecurringInflows">
            <Field.Label>Monthly contributions</Field.Label>
            <Field.Control>
              <Input
                leadingSymbol="$"
                mask="number"
                placeholder="0"
                aria-label="Monthly contributions amount"
                onChange={handleFormChange}
              />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
        </Box>
        {values.monthlyRecurringDeposits &&
          hasMaskedFormFieldChanged(values.monthlyRecurringDeposits, initialValues.monthlyRecurringDeposits) && (
            <SambaAutoLink to={handleSetupRecurringDeposit(subAccountId, values.monthlyRecurringDeposits)}>
              Continue to deposit
            </SambaAutoLink>
          )}
      </Box>
    </Form>
  );
};

type Props = {
  account: NarrowTypename<ForecasterAccountSettingsQuery['account'], 'ManagedAccount'>;
};

export const AccountSettingsForm = ({ account }: Props) => {
  const { updateFormInputs } = useForecasterAccountInputs();

  const { monthlyNetRecurringCashflows, recurringTransactionConfigs, subAccounts } = account;
  const { initialEditableAmount } = calculateCashflows({
    monthlyNetRecurringCashflows,
    legacyTransferType: LegacyTransferType.Deposit,
    configs: recurringTransactionConfigs,
  });

  return (
    <Formik
      onSubmit={values =>
        updateFormInputs({
          adHocInflow: convertToMoneyWithDefault(values.adHocInflow),
          monthlyRecurringInflows: convertToMoneyWithDefault(values.monthlyRecurringInflows),
        })
      }
      validationSchema={schema}
      initialValues={{
        adHocInflow: '',
        monthlyRecurringInflows: initialEditableAmount?.cents ? formatMoney(initialEditableAmount, 'raw') : '',
      }}
    >
      <FormikForm subAccountId={subAccounts[0].legacySubAccountId.toString()} />
    </Formik>
  );
};
